import MatchEnum from "~src/enums/volleyball/MatchEnum";
import StatsEnum from "~src/enums/volleyball/StatsEnum";
import { isObjectNotEmpty } from "..";

export const initializeData = () => ({
  score: { home: 0, away: 0 },
  homeData: StatsEnum.statsInit,
  awayData: StatsEnum.statsInit,
  id: "",
  current_type: 0,
  status_id: 0,
  type: 100,
});

const calculatePercentage = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};

const chooseValue = (statistics: any, key: string, index: number) => {
  let teamData = index === 1 ? "homeData" : "awayData";
  let data;
  if (key === "service_points_success") {
    data = `${statistics[teamData].service_points_success}/${statistics[teamData].service_points_total}`;
  } else if (key === "receiver_points_success") {
    data = `${statistics[teamData].receiver_points_success}/${statistics[teamData].receiver_points_total}`;
  } else {
    data = `${statistics[teamData][key] ?? 0}`;
  }

  return data;
};

export const getProcessedStats = (
  match: any,
  statistics: any,
  isDefault: boolean
) => {
  const techKind: Record<string, string> = StatsEnum.techKind;

  return Object.keys(techKind).map((forwardKey) => {
    let key = forwardKey;

    let showAwayPre = calculatePercentage(
      Number(chooseValue(statistics, key, 2)),
      Number(chooseValue(statistics, key, 1)) +
        Number(chooseValue(statistics, key, 2))
    );
    let showHomePre = calculatePercentage(
      Number(chooseValue(statistics, key, 1)),
      Number(chooseValue(statistics, key, 1)) +
        Number(chooseValue(statistics, key, 2))
    );
    let winHome = Number(chooseValue(statistics, key, 1));
    let winAway = Number(chooseValue(statistics, key, 2));

    let home = chooseValue(statistics, forwardKey, 1);
    let away = chooseValue(statistics, forwardKey, 2);

    return {
      key,
      type: techKind[forwardKey],
      home,
      away,
      showAwayPre,
      showHomePre,
      winHome: winHome > winAway ? "as-statistics__home-win" : "",
      winAway: winAway > winHome ? "as-statistics__away-win" : "",
    };
  });
};

const getTechnicalStatistics = (data: Array<any>, code: number) => {
  if (data) {
    const result = data.find((item: any) => item[0] === code);
    return result || [code, 0, 0];
  }

  return [code, 0, 0];
};

const calculateTeamData = (stats: any, index: any) => {
  try {
    let homeData = StatsEnum.statsInit;
    let awayData = StatsEnum.statsInit;
    if (!stats) {
      return { homeData, awayData };
    }
    if (stats) {
      homeData = {
        aces: getTechnicalStatistics(stats, MatchEnum.ACES)[1],
        max_points_in_a_row: getTechnicalStatistics(
          stats,
          MatchEnum.MAX_POINTS_IN_A_ROW
        )[1],
        points_won: getTechnicalStatistics(stats, MatchEnum.POINTS_WON)[1],
        service_errors: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_ERRORS
        )[1],
        timeouts: getTechnicalStatistics(stats, MatchEnum.TIMEOUTS)[1],
        service_points_success: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_SUCCESS
        )[1],
        service_points_total: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_TOTAL
        )[1],
        service_points_rate: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_RATE
        )[1],
        receiver_points_success: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_SUCCESS
        )[1],
        receiver_points_total: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_TOTAL
        )[1],
        receiver_points_rate: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_RATE
        )[1],
      };

      awayData = {
        aces: getTechnicalStatistics(stats, MatchEnum.ACES)[2],
        max_points_in_a_row: getTechnicalStatistics(
          stats,
          MatchEnum.MAX_POINTS_IN_A_ROW
        )[2],
        points_won: getTechnicalStatistics(stats, MatchEnum.POINTS_WON)[2],
        service_errors: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_ERRORS
        )[2],
        timeouts: getTechnicalStatistics(stats, MatchEnum.TIMEOUTS)[2],
        service_points_success: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_SUCCESS
        )[2],
        service_points_total: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_TOTAL
        )[2],
        service_points_rate: getTechnicalStatistics(
          stats,
          MatchEnum.SERVICE_POINTS_RATE
        )[2],
        receiver_points_success: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_SUCCESS
        )[2],
        receiver_points_total: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_TOTAL
        )[2],
        receiver_points_rate: getTechnicalStatistics(
          stats,
          MatchEnum.RECEIVER_POINTS_RATE
        )[2],
      };
    }

    return { homeData, awayData };
  } catch (error) {
    throw error;
  }
};

export const mapMatchData = (matchData: any) => {
  try {
    if (!isObjectNotEmpty(matchData)) return [];

    let _status = [...MatchEnum.LIST_PLAYING, ...[MatchEnum.ENDED]];
    let matches = matchData
      .filter((m: any) => {
        return _status.includes(m.status_id);
      })
      .sort((a: any, b: any) => {
        return a.status_id - b.status_id > 0 ? 1 : -1;
      });

    const mapStatus = {
      "0": 0,
      "1": MatchEnum.FIRST_SET,
      "2": MatchEnum.SECOND_SET,
      "3": MatchEnum.THIRD_SET,
      "4": MatchEnum.FOURTH_SET,
      "5": MatchEnum.FIFTH_SET,
    };
    matches = matches[matches.length - 1];

    const { stats } = matches;
    const t = stats.map((stat: any) => {
      let [index, detailStat]: [keyof typeof mapStatus, any] = stat;
      const { homeData, awayData } = calculateTeamData(detailStat, index);
      return {
        status_id: mapStatus[index],
        homeData,
        awayData,
      };
    });

    return t;
  } catch (error) {
    throw error;
  }
};
